/**
 * Decoding the binary vectors the data API returns.
 *
 * Two layouts come back, and which one depends on the key:
 *
 *   Date/Time        float64, milliseconds since the epoch
 *   everything else  float32
 *
 * Nothing in the payload says which, so a reader that assumes the wrong one gets
 * numbers that are perfectly valid and completely wrong. Reading the timestamps as
 * the wrong width is what produced values like 8.575e-312.
 */

/** Smallest positive normal double. Below this, and non-zero, is subnormal. */
const SMALLEST_NORMAL = 2.2250738585072014e-308;

export class BinaryDecodeError extends Error {}

const toArrayBuffer = async (response: Blob | ArrayBuffer): Promise<ArrayBuffer> =>
  response instanceof Blob ? await response.arrayBuffer() : response;

/**
 * Milliseconds since the epoch, as a plain array.
 *
 * Deliberately not ISO strings. On forty years of hourly data the string form costs
 * around 21 MB and half a second of blocked main thread, against 2.7 MB for the
 * numbers - and Plotly, comparisons and arithmetic all want numbers anyway. Format
 * only the labels actually rendered.
 */
export const decodeTimeline = async (response: Blob | ArrayBuffer): Promise<number[]> => {
  const buffer = await toArrayBuffer(response);

  if (buffer.byteLength === 0) return [];
  if (buffer.byteLength % 8 !== 0) {
    // Float64Array throws on this, and the message says nothing about the cause.
    throw new BinaryDecodeError(
      `Timeline payload is ${buffer.byteLength} bytes, which is not a whole number of float64 values.`,
    );
  }

  const millis = new Float64Array(buffer);

  /*
   * Checks the shape of the number, not the date it represents.
   *
   * A range of plausible dates would be the wrong test: reanalysis reaches back to
   * 1940 and projections run forward indefinitely, so any range wide enough to be
   * safe cannot discriminate. Integer bytes read as a double come out subnormal, or
   * NaN when negative, and neither is something a real timestamp can be in any
   * century.
   */
  const first = millis[0];
  if (first !== 0 && (!Number.isFinite(first) || Math.abs(first) < SMALLEST_NORMAL)) {
    throw new BinaryDecodeError(
      `Timeline values are not float64 timestamps (first = ${first}). The payload dtype has changed.`,
    );
  }

  return Array.from(millis);
};

/** Vector data. float32 on the wire, widened to double on the way into JS. */
export const decodeVector = async (response: Blob | ArrayBuffer): Promise<number[]> => {
  const buffer = await toArrayBuffer(response);

  if (buffer.byteLength === 0) return [];
  if (buffer.byteLength % 4 !== 0) {
    throw new BinaryDecodeError(
      `Vector payload is ${buffer.byteLength} bytes, which is not a whole number of float32 values.`,
    );
  }

  return Array.from(new Float32Array(buffer));
};